import { A4_PREVIEW_WIDTH_PX } from "@/components/ui/report/TemplateStudioEmptyCanvas/TemplateStudioEmptyCanvas";
import { isReportBannerImage } from "@/lib/report/prepareReportSpecForRender";
import type { TemplateStudioDraft } from "@/lib/template-studio/draft/templateStudioDraft";

const getBannerSrcs = (draft: TemplateStudioDraft | null | undefined) =>
  new Set(
    Object.values(draft?.spec.elements ?? {})
      .filter(isReportBannerImage)
      .map((element) => String(element.props?.src ?? ""))
      .filter((src) => src !== ""),
  );

/** Banner image srcs in the next draft that the prior draft did not already use as a banner. */
export const findNewBannerSrcs = (
  priorDraft: TemplateStudioDraft | null | undefined,
  nextDraft: TemplateStudioDraft,
): string[] => {
  const priorSrcs = getBannerSrcs(priorDraft);
  return [...getBannerSrcs(nextDraft)].filter((src) => !priorSrcs.has(src));
};

/** A 3:1 banner at page width; taller ones still show whole, but push the report down the page. */
const RECOMMENDED_BANNER_HEIGHT_PX = Math.round(A4_PREVIEW_WIDTH_PX / 3);

interface ImageSize {
  width: number;
  height: number;
}

/**
 * Recommends the banner size when an image makes the banner taller than recommended at
 * page width. Null when the banner is short enough.
 */
export const resolveBannerSizeTip = ({
  width,
  height,
}: ImageSize): string | null => {
  if (!(width > 0) || !(height > 0)) return null;
  const heightAtPageWidth = (A4_PREVIEW_WIDTH_PX * height) / width;
  if (heightAtPageWidth <= RECOMMENDED_BANNER_HEIGHT_PX) return null;

  return `Tip: this image is ${width} × ${height} px, so the banner fills the page width at ${A4_PREVIEW_WIDTH_PX} × ${Math.round(heightAtPageWidth)} px and pushes the report down. For a shorter banner, use an image ${A4_PREVIEW_WIDTH_PX} × ${RECOMMENDED_BANNER_HEIGHT_PX} px, or larger in the same shape.`;
};

/** Natural size of an image URL; null if it fails to load in time. Reading the size needs no CORS. */
export const loadImageSize = (
  src: string,
  signal: AbortSignal,
): Promise<ImageSize | null> =>
  new Promise((resolve) => {
    if (signal.aborted) {
      resolve(null);
      return;
    }
    const image = new Image();
    const finish = (size: ImageSize | null) => {
      signal.removeEventListener("abort", onAbort);
      resolve(size);
    };
    const onAbort = () => finish(null);
    signal.addEventListener("abort", onAbort, { once: true });
    image.onload = () =>
      finish({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => finish(null);
    image.src = src;
  });
